"use strict";
import { REHYDRATE, PERSIST } from "redux-persist";
import axios from "../../util/axios";
import { createSelector } from "reselect";
import { omit } from "lodash";

import {
  FETCH_STARTED,
  FETCH_FAILED,
  FETCH_ENDED,
  RESET,
  SET_SEARCH_TERM,
  SET_SEARCH_TERM_INITIAL,
  SET_SEARCH_TABLE,
  SET_FILTER,
  SET_LIMIT,
  SET_MANUFACTURER,
  SET_PART_TYPE,
  SET_SPARE_PART_TYPE,
  SET_SEARCH_LOCATION,
  PAGER_DEFAULT_DIR,
  PAGER_DEFAULT_SORT,
  SAVE_FILTER_SETTINGS,
  RESTORE_FILTER_SETTINGS,
} from "./constants";

import { default as pager } from "./pager";
import { default as lastSearches } from "./lastSearches";

const fetchStart = () => ({ type: FETCH_STARTED });
const fetchEnd = (data) => ({ type: FETCH_ENDED, payload: data });
const fetchFail = (error) => ({ type: FETCH_FAILED, payload: error, error: true });

export const setFilter = (filter) => ({ type: SET_FILTER, payload: filter });
export const setManufacturer = (manufacturer) => ({ type: SET_MANUFACTURER, payload: manufacturer });
export const setSparePartType = (sparePartType) => ({ type: SET_SPARE_PART_TYPE, payload: sparePartType });
export const setPartType = (partType) => ({ type: SET_PART_TYPE, payload: partType });

export const setSearchLocation = (searchLocation) => ({ type: SET_SEARCH_LOCATION, payload: searchLocation });
export const setSearchTerm = (searchTerm, initial) => ({
  type: initial ? SET_SEARCH_TERM_INITIAL : SET_SEARCH_TERM,
  payload: searchTerm,
});
export const setSearchTable = (searchTable) => ({ type: SET_SEARCH_TABLE, payload: searchTable });

export const reset = () => ({ type: RESET });

export const saveFilterSettings = (data) => ({ type: SAVE_FILTER_SETTINGS, payload: data });
const _restoreFilterSettings = (hash) => ({ type: RESTORE_FILTER_SETTINGS, payload: hash });

const initialState = {
  isFetching: false,
  error: null,
  materials: [],
  filters: [],
  filterGroups: [],
  manufacturer: undefined,
  sparePartType: undefined,
  partType: undefined,
  filterSelection: {},
  searchTerm: "",
  searchLocation: undefined,
  searchTable: {},
  pager: undefined, //pager(undefined, {}),
  lastSearches: undefined,
  savedFilterSettings: [],
};

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_STARTED:
      return {
        ...state,
        isFetching: true,
        error: null,
      };

    case FETCH_ENDED:
      return {
        ...state,
        materials: action.payload.materials,
        searchTerm: action.payload.searchTerm || "",
        searchTable: action.payload.searchTable || {},
        filters: action.payload.filters,
        filterGroups: action.payload.filterGroups,
        filterSelection: action.payload.filterSelection,
        pager: pager(state.pager, action),
        isFetching: false,
        error: null,
      };

    case FETCH_FAILED:
      return {
        ...state,
        isFetching: false,
        error: action.payload,
      };

    case SET_FILTER:
      return {
        ...state,
        filterSelection: action.payload,
      };

    case SET_SEARCH_TERM:
      return {
        ...state,
        searchTable: {},
        searchTerm: action.payload,
      };

    case SET_SEARCH_TERM_INITIAL:
      return {
        ...state,
        filterSelection: {},
        searchTable: {},
        searchTerm: action.payload,
        lastSearches: lastSearches(state.lastSearches, action),
      };

    case SET_SEARCH_TABLE:
      return {
        ...state,
        searchTable: action.payload,
        searchTerm: "",
      };

    case SET_SEARCH_LOCATION:
      return {
        ...state,
        searchLocation: action.payload,
      };

    case SET_MANUFACTURER:
      return {
        ...state,
        manufacturer: action.payload,
      };

    case SET_SPARE_PART_TYPE:
      return {
        ...state,
        sparePartType: action.payload,
      };

    case SET_PART_TYPE:
      return {
        ...state,
        partType: action.payload,
      };

    case SET_LIMIT:
      return {
        ...state,
        pager: pager(state.pager, action),
      };

    case RESET:
      return {
        ...initialState,
        pager: pager(state.pager, action),
        lastSearches: lastSearches(state.lastSearches, action),
        savedFilterSettings: state.savedFilterSettings,
      };

    case SAVE_FILTER_SETTINGS:
      return (function saveFilterSettings() {
        const hash = action.payload.hash;
        const content = JSON.stringify(action.payload.content);

        // append the new setting, but prevent duplicates
        const settings = state.savedFilterSettings.filter((s) => s.hash !== hash).concat([{ hash, content }]);

        // ring buffer for saved settings
        const savedFilterSettings = settings.slice(Math.max(0, settings.length - 5), settings.length);
        return { ...state, savedFilterSettings };
      })();

    case RESTORE_FILTER_SETTINGS:
      return (function restoreFilterSettings() {
        const hash = action.payload;

        const savedSettings = state.savedFilterSettings.find((fs) => fs.hash === hash);

        if (!savedSettings) {
          return state;
        }

        const content = JSON.parse(savedSettings.content);

        return {
          ...state,
          filterSelection: content.filterSelection,
          pager: { ...content.pager, count: 0 },
          searchTable: content.searchTable,
          searchTerm: content.searchTerm,
        };
      })();

    case PERSIST:
    case REHYDRATE:
      return {
        ...state,
        pager: pager(state.pager, action),
        lastSearches: lastSearches(state.lastSearches, action),
      };

    default:
      return state;
  }
}
// selectors

const dataSelector = (state) => state.materials;

export const getIsFetching = createSelector(dataSelector, (data) => data.isFetching);

export const getHasError = createSelector(dataSelector, (data) => !!data.error);

export const getError = createSelector(dataSelector, (data) => data.error);

export const getMaterials = createSelector(dataSelector, (data) => data.materials);

export const getSearchTerm = createSelector(dataSelector, (data) => data.searchTerm);

export const getLastSearches = createSelector(dataSelector, (data) => data.lastSearches);

export const getLastSearchTerms = createSelector(getLastSearches, (data) => data.terms || []);

export const getSearchTable = createSelector(dataSelector, (data) => data.searchTable);

export const getSearchLocation = createSelector(dataSelector, (data) => data.searchLocation);

export const getFilters = createSelector(dataSelector, (data) => data.filters);

export const getFilterGroups = createSelector(dataSelector, (data) => data.filterGroups);

export const getManufacturer = createSelector(dataSelector, (data) => data.manufacturer);

export const getSparePartType = createSelector(dataSelector, (data) => data.sparePartType);

export const getPartType = createSelector(dataSelector, (data) => data.partType);

export const getFilterSelection = createSelector(dataSelector, (data) => data.filterSelection);

export const getPager = createSelector(dataSelector, (data) => omit(data.pager, "_persist"));

export function restoreFilterSettings(hash) {
  return (dispatch, getState) => {
    dispatch(_restoreFilterSettings(hash));
    const { offset, limit, sort, dir } = getPager(getState());
    dispatch(fetchMaterials({ offset, limit, sort, dir }));
  };
}

export function fetchMaterials(query) {
  return (dispatch, getState) => {
    const state = getState();

    if (getIsFetching(state)) {
      return;
    }

    const p = getPager(state);

    dispatch(fetchStart());
    axios
      .post("api/materials/filter", {
        query: {
          filter: getFilterSelection(state),
          searchTerm: getSearchTerm(state),
          searchTable: getSearchTable(state),
          manufacturer: getManufacturer(state),
          category: getSparePartType(state), // || getPartType(state), // doesn't work for orthoscan
          partType: getPartType(state),
          // default pager settings
          offset: 0,
          sort: PAGER_DEFAULT_SORT,
          dir: PAGER_DEFAULT_DIR,
          limit: p.limit,
          // query can override other properties !
          ...query,
        },
      })
      .then((res) => {
        dispatch(fetchEnd(res.data));
      })
      .catch((err) => {
        let uiError = new Error(err.message);
        if (err.response) {
          uiError.status = err.response.status;
        }
        dispatch(fetchFail(uiError));
      });
  };
}
